/**
 * Server state of boards, columns and cards on a board.
 *
 * The board detail query (`queryKeys.boards.detail`) is the single client-side
 * copy of a board. Mutations update it from server responses; moves and
 * completion are optimistic with rollback on error, followed by a refetch so
 * the server stays authoritative (ARCHITECTURE.md §16).
 */
import { useEffect } from 'react';

import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';
import type { Look } from '@/entities/appearance';
import { burstConfetti } from '@/shared/lib/confetti';

import {
  type CardTarget,
  cardMoveRequest,
  type ColumnTarget,
  columnMoveRequest,
  moveCardLocally,
  moveColumnLocally,
  setPinnedLocally,
} from './model';
import type {
  Board,
  BoardDetail,
  Card,
  CardFilter,
  Column,
  ColumnAppearance,
  Label,
  LinkPreview,
} from './types';

/** A card with its content blocks (`entities/card`); only the cache key matters here. */
type CardDetail = Card & { content_blocks: unknown[] };

export function useBoards(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.boards.list(workspaceId),
    queryFn: ({ signal }) => api.get<Board[]>(`/v1/workspaces/${workspaceId}/boards`, { signal }),
    enabled,
  });
}

/** `GET /boards/{id}/cards`: server-side search/filter (only when a filter is active). */
export function useBoardCardSearch(boardId: string, filter: CardFilter, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.boards.search(boardId, filter),
    queryFn: ({ signal }) =>
      api.get<Card[]>(`/v1/boards/${boardId}/cards`, {
        signal,
        query: {
          q: filter.q.trim() || undefined,
          // Both "open" and "done" (or neither) restrict nothing.
          completed: filter.status.length === 1 ? filter.status[0] === 'done' : undefined,
          label_match:
            filter.labelIds.length > 1 && filter.labelMatch === 'all' ? 'all' : undefined,
        },
        repeat: {
          label_id: filter.labelIds,
          deadline: filter.deadline,
          assignment: filter.assignment,
        },
      }),
    enabled,
    placeholderData: (previous) => previous,
  });
}

/**
 * `GET /boards/{id}/timeline`: cards of this board with a deadline, soonest first.
 * Grouping (`deadline.state`) is decided by the server. `week` (inclusive local dates
 * `YYYY-MM-DD`) keeps the cards whose period overlaps it plus every overdue card.
 */
export function useBoardTimeline(
  boardId: string,
  includeCompleted: boolean,
  week: { first: string; last: string },
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.boards.timeline(boardId, includeCompleted, week.first, week.last),
    queryFn: ({ signal }) =>
      api.get<Card[]>(`/v1/boards/${boardId}/timeline`, {
        signal,
        query: {
          include_completed: includeCompleted,
          first: week.first,
          last: week.last,
        },
      }),
    enabled,
    placeholderData: (previous) => previous,
  });
}

/** Board structure changed: refetch the board, open search results, Timeline, archive and trash. */
export function invalidateBoard(client: QueryClient, boardId: string): Promise<void> {
  return Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) }),
    client.invalidateQueries({ queryKey: queryKeys.boards.searchAll(boardId) }),
    client.invalidateQueries({ queryKey: queryKeys.boards.timelineAll(boardId) }),
    client.invalidateQueries({ queryKey: queryKeys.calendar.all }),
    client.invalidateQueries({ queryKey: queryKeys.boards.archive(boardId) }),
    client.invalidateQueries({ queryKey: queryKeys.boards.trash(boardId) }),
  ]).then(() => undefined);
}

export function useBoardDetail(boardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.boards.detail(boardId),
    queryFn: ({ signal }) => api.get<BoardDetail>(`/v1/boards/${boardId}`, { signal }),
    enabled: enabled && boardId !== '',
  });
}

/** Warm a board on intent (hover/focus of its link); skipped while a fresh copy is cached. */
export function usePrefetchBoard() {
  const client = useQueryClient();
  return (boardId: string) =>
    client
      .query({
        queryKey: queryKeys.boards.detail(boardId),
        queryFn: ({ signal }) => api.get<BoardDetail>(`/v1/boards/${boardId}`, { signal }),
      })
      .catch(() => undefined);
}

const PREVIEW_POLL_MS = 3000;
const PREVIEW_POLL_MAX_MS = 20_000;
/** A preview still pending after this long is left alone (a manual refresh restarts it). */
const PREVIEW_POLL_BUDGET_MS = 120_000;
const PREVIEW_POLL_BATCH = 10;

/**
 * Link previews are fetched by the server after a card is saved. Instead of re-reading the whole
 * board, ask only for the pending cards' previews (the permission-checked
 * `GET /cards/:id/link-preview`), with growing delays, a hard time budget, no requests while the
 * tab is hidden, and the result written into the cached board. Mount it once per board page.
 */
export function useLinkPreviewPolling(boardId: string, detail: BoardDetail | undefined): void {
  const client = useQueryClient();
  const pending = (detail?.cards ?? [])
    .filter((card) => card.link_preview?.status === 'pending')
    .map((card) => card.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (pending === '') return;
    const ids = new Set(pending.split(','));
    const startedAt = Date.now();
    const controller = new AbortController();
    let delay = PREVIEW_POLL_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (Date.now() - startedAt > PREVIEW_POLL_BUDGET_MS) return;
      if (!document.hidden) {
        const batch = [...ids].slice(0, PREVIEW_POLL_BATCH);
        const results = await Promise.allSettled(
          batch.map((id) =>
            api.get<LinkPreview>(`/v1/cards/${id}/link-preview`, { signal: controller.signal }),
          ),
        );
        if (controller.signal.aborted) return;
        const done = new Map<string, LinkPreview>();
        results.forEach((result, index) => {
          const id = batch[index];
          if (id === undefined) return;
          if (result.status === 'rejected')
            ids.delete(id); // gone or not a link any more
          else if (result.value.status !== 'pending') {
            ids.delete(id);
            done.set(id, result.value);
          }
        });
        if (done.size > 0) {
          client.setQueryData<BoardDetail>(queryKeys.boards.detail(boardId), (current) =>
            current
              ? {
                  ...current,
                  cards: current.cards.map((card) => {
                    const preview = done.get(card.id);
                    return preview ? { ...card, link_preview: preview } : card;
                  }),
                }
              : current,
          );
        }
      }
      if (ids.size === 0) return;
      delay = Math.min(delay * 1.5, PREVIEW_POLL_MAX_MS);
      timer = setTimeout(() => void tick(), delay);
    };
    timer = setTimeout(() => void tick(), delay);
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [pending, boardId, client]);
}

export function updateDetail(
  client: QueryClient,
  boardId: string,
  update: (detail: BoardDetail) => BoardDetail,
): void {
  client.setQueryData<BoardDetail>(queryKeys.boards.detail(boardId), (current) =>
    current ? update(current) : current,
  );
  // Any board change may change which cards match an active filter or the Timeline.
  void client.invalidateQueries({ queryKey: queryKeys.boards.searchAll(boardId) });
  void client.invalidateQueries({ queryKey: queryKeys.boards.timelineAll(boardId) });
  void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
}

/** Replace a card in the board detail with a fresh server copy (keeps position). */
export function syncCardInBoard(client: QueryClient, card: Card): void {
  const {
    id,
    title,
    completed_at,
    version,
    updated_at,
    label_ids,
    checklist_progress,
    checklists,
    deadline,
    is_overdue,
    cover,
    appearance,
    attachment_count,
    link_preview,
  } = card;
  updateDetail(client, card.placement.board_id, (detail) => ({
    ...detail,
    cards: detail.cards.map((c) =>
      c.id === id
        ? {
            ...c,
            title,
            completed_at,
            version,
            updated_at,
            label_ids,
            checklist_progress,
            checklists,
            deadline,
            is_overdue,
            cover,
            appearance,
            attachment_count,
            link_preview,
          }
        : c,
    ),
  }));
}

/**
 * Optimistic update helper: apply `change` to the board detail now; restore the
 * snapshot on error; refetch afterwards.
 */
function useOptimisticBoardMutation<V>(
  boardId: string,
  request: (variables: V) => Promise<unknown>,
  change: (detail: BoardDetail, variables: V) => BoardDetail | null,
) {
  const client = useQueryClient();
  const key = queryKeys.boards.detail(boardId);
  return useMutation({
    mutationFn: request,
    onMutate: async (variables: V) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<BoardDetail>(key);
      const next = previous ? change(previous, variables) : null;
      if (next) client.setQueryData(key, next);
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSettled: () => invalidateBoard(client, boardId),
  });
}

// --- boards ---------------------------------------------------------------

/** Initial collaboration settings of a new TEAM board; created in one transaction. */
export interface NewBoardInput {
  name: string;
  description?: string | null;
  /** "Created on behalf of" team: drives owner succession, grants no access by itself. */
  origin_team_id?: string | null;
  /** File the new board in this section (UD-STRUCT) in the same request; omitted = the root. */
  section_id?: string | null;
  label_ids?: string[];
  /** Teams that may open the board, with the role each gets (never `owner`). */
  team_access?: { team_id: string; role: string }[];
  /** Workspace roles (standard or custom) that may open the board, with the board role each gets. */
  role_access?: { subject_role?: string; custom_role_id?: string; role: string }[];
  /** Workspace members invited with an explicit role (never `owner`). */
  participants?: { user_id: string; role: string }[];
}

export function useCreateBoard(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: NewBoardInput) =>
      api.post<Board>(`/v1/workspaces/${workspaceId}/boards`, data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.collab.myBoards });
      return client.invalidateQueries({ queryKey: queryKeys.boards.list(workspaceId) });
    },
  });
}

export function useUpdateBoard(board: Board) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name?: string;
      description?: string | null;
      color?: string | null;
      /** An active column of this board; `null` = the first active column. */
      default_column_id?: string | null;
      /** Working days of the Calendar's Work week: distinct ISO weekdays 1-7, at least one. */
      workweek_days?: number[];
    }) => api.patch<Board>(`/v1/boards/${board.id}`, { ...data, expected_version: board.version }),
    onSuccess: (updated) => {
      updateDetail(client, board.id, (detail) => ({ ...detail, board: updated }));
      if (board.workspace_id) {
        void client.invalidateQueries({ queryKey: queryKeys.boards.list(board.workspace_id) });
      }
      void client.invalidateQueries({ queryKey: queryKeys.collab.myBoards });
    },
    onError: () => client.invalidateQueries({ queryKey: queryKeys.boards.detail(board.id) }),
  });
}

/**
 * Reorders a board among its workspace's boards. The request names the board and its NEIGHBOR by
 * id (never an index), so a list that went stale cannot move a different board; the lists are read
 * again afterwards and the server's answer is the order.
 */
export function useMoveBoard(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...anchor }: { id: string; after_id?: string; before_id?: string }) =>
      api.put<Board>(`/v1/boards/${id}/position`, anchor),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.boards.list(workspaceId) }),
  });
}

export function useDeleteBoard(board: Board) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/v1/boards/${board.id}`),
    onSuccess: () => {
      client.removeQueries({ queryKey: queryKeys.boards.detail(board.id) });
      if (board.workspace_id) {
        void client.invalidateQueries({ queryKey: queryKeys.boards.list(board.workspace_id) });
      }
    },
  });
}

export function useToggleFavorite(
  board: Pick<Board, 'id' | 'is_favorite'> & { workspace_id: string | null },
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (favorite: boolean) =>
      favorite
        ? api.put(`/v1/boards/${board.id}/favorite`)
        : api.delete(`/v1/boards/${board.id}/favorite`),
    onSettled: async () => {
      if (board.workspace_id) {
        await client.invalidateQueries({ queryKey: queryKeys.boards.list(board.workspace_id) });
      }
      await client.invalidateQueries({ queryKey: queryKeys.boards.detail(board.id) });
      await client.invalidateQueries({ queryKey: queryKeys.collab.myBoards });
    },
  });
}

// --- columns --------------------------------------------------------------

export function useCreateColumn(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => api.post<Column>(`/v1/boards/${boardId}/columns`, data),
    onSuccess: (column) => {
      updateDetail(client, boardId, (detail) => ({
        ...detail,
        columns: [...detail.columns, column],
      }));
    },
  });
}

export function useUpdateColumn(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      columnId,
      ...data
    }: {
      columnId: string;
      name?: string;
      /** Markdown; `null` or blank clears it. */
      description?: string | null;
      /** `#rrggbb`; `null` removes the color. */
      color?: string | null;
      appearance_mode?: ColumnAppearance;
    }) => api.patch<Column>(`/v1/columns/${columnId}`, data),
    onSuccess: (column) => {
      updateDetail(client, boardId, (detail) => ({
        ...detail,
        columns: detail.columns.map((c) => (c.id === column.id ? column : c)),
      }));
    },
  });
}

export function useMoveColumn(boardId: string) {
  return useOptimisticBoardMutation(
    boardId,
    ({ columnId, target }: { columnId: string; target: ColumnTarget }) =>
      api.post<Column>(`/v1/columns/${columnId}/move`, columnMoveRequest(target)),
    (detail, { columnId, target }) => moveColumnLocally(detail, columnId, target),
  );
}

/** Copies the column with its active cards (one backend transaction). */
export function useCopyColumn(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (columnId: string) =>
      api.post<{ column: Column; cards: Card[] }>(`/v1/columns/${columnId}/copy`),
    onSuccess: () => invalidateBoard(client, boardId),
  });
}

export function useDeleteColumn(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (columnId: string) => api.delete(`/v1/columns/${columnId}`),
    onSuccess: (_data, columnId) => {
      updateDetail(client, boardId, (detail) => ({
        ...detail,
        columns: detail.columns.filter((c) => c.id !== columnId),
        cards: detail.cards.filter((c) => c.placement.column_id !== columnId),
      }));
      void client.invalidateQueries({ queryKey: queryKeys.boards.trash(boardId) });
    },
  });
}

// --- cards on the board ---------------------------------------------------

/** A content block of a new card: Markdown text or a checklist. */
export type NewCardBlock =
  { markdown_text: string } | { checklist: { title: string | null; items: string[] } };

/** Recurrence schedule (see `entities/recurrence`); sent along with a new card. */
export interface RecurrenceInput {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  weekdays: number[];
  start_date?: string | null;
  time_of_day?: string | null;
  until_date?: string | null;
  timezone?: string | null;
  column_id?: string | null;
}

/** Everything a new card can be created with in ONE atomic request. */
export interface CardCreateInput {
  columnId: string;
  title: string;
  deadline?: DeadlineInput | null;
  label_ids?: string[];
  appearance?: Look | null;
  cover?: { kind: 'color'; color: string } | null;
  content?: NewCardBlock[];
  recurrence?: RecurrenceInput | null;
}

export function useCreateCard(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, ...data }: CardCreateInput) =>
      api.post<Card>(`/v1/columns/${columnId}/cards`, data),
    onSuccess: (card) => {
      updateDetail(client, boardId, (detail) => {
        const lastIndex = detail.cards.findLastIndex(
          (c) => c.placement.column_id === card.placement.column_id,
        );
        const index = lastIndex === -1 ? detail.cards.length : lastIndex + 1;
        return {
          ...detail,
          cards: [...detail.cards.slice(0, index), card, ...detail.cards.slice(index)],
        };
      });
    },
  });
}

export type SortCriterion =
  | 'title_asc'
  | 'title_desc'
  | 'deadline_asc'
  | 'deadline_desc'
  | 'created_asc'
  | 'created_desc'
  | 'incomplete_first'
  | 'completed_first';

/**
 * One-time permanent rearrangement of a column's cards (server side, whole column). Not a mode:
 * the board is simply refetched in its new order.
 */
export function useSortColumn(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, criterion }: { columnId: string; criterion: SortCriterion }) =>
      api.post<{ changed: boolean; card_ids: string[] }>(`/v1/columns/${columnId}/sort`, {
        criterion,
      }),
    onSuccess: () => invalidateBoard(client, boardId),
  });
}

/** Show/hide the system Calendar column in the caller's Kanban (a per-user, per-board view setting). */
export function useSetCalendarColumnVisible(boardId: string) {
  return useOptimisticBoardMutation(
    boardId,
    (visible: boolean) =>
      visible
        ? api.put(`/v1/boards/${boardId}/calendar-column`)
        : api.delete(`/v1/boards/${boardId}/calendar-column`),
    (detail, visible) => ({ ...detail, show_calendar_column: visible }),
  );
}

/** A new independent card right after the source; the board is refetched for its place. */
export function useCopyCard(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) => api.post<Card>(`/v1/cards/${cardId}/copy`),
    onSuccess: () => invalidateBoard(client, boardId),
  });
}

export function useMoveCard(boardId: string) {
  const client = useQueryClient();
  return useOptimisticBoardMutation(
    boardId,
    async ({ cardId, target }: { cardId: string; target: CardTarget }) => {
      const card = await api.post<Card>(`/v1/cards/${cardId}/move`, cardMoveRequest(target));
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
      return card;
    },
    (detail, { cardId, target }) => moveCardLocally(detail, cardId, target),
  );
}

export function useSetCardCompleted(boardId: string) {
  const client = useQueryClient();
  return useOptimisticBoardMutation(
    boardId,
    async ({
      cardId,
      completed,
      anchor,
    }: {
      cardId: string;
      completed: boolean;
      /** The control that was pressed: the confetti bursts around it once the server agrees. */
      anchor?: Element | null;
    }) => {
      const card = await api.post<Card>(
        `/v1/cards/${cardId}/${completed ? 'complete' : 'uncomplete'}`,
      );
      // Only after the server confirmed, and only for completing (never for reopening).
      if (completed) burstConfetti(anchor);
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
      return card;
    },
    // Completion only marks the card; it stays on the board (archiving is separate).
    (detail, { cardId, completed }) => ({
      ...detail,
      cards: detail.cards.map((c) =>
        c.id === cardId ? { ...c, completed_at: completed ? new Date().toISOString() : null } : c,
      ),
    }),
  );
}

/** Archive/unarchive. Archived cards leave the board; completed and pinned state is kept. */
export function useSetCardArchived(boardId: string) {
  const client = useQueryClient();
  return useOptimisticBoardMutation(
    boardId,
    async ({ cardId, archived }: { cardId: string; archived: boolean }) => {
      const card = await api.post<Card>(
        `/v1/cards/${cardId}/${archived ? 'archive' : 'unarchive'}`,
      );
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
      return card;
    },
    // Restoring brings the card back with the next refetch, at its old place.
    (detail, { cardId, archived }) =>
      archived ? { ...detail, cards: detail.cards.filter((c) => c.id !== cardId) } : null,
  );
}

/** The END (`date`, optional `time`) and an optional period START, all local. */
export interface DeadlineInput {
  date: string;
  time?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  /** Events only; absent keeps what the card has. */
  overdue_enabled?: boolean;
}

/**
 * Set (END, optional START) or remove (`null`) the deadline. The server interprets the
 * date/time in the user's profile timezone and returns the derived state.
 */
export function useSetCardDeadline() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, deadline }: { cardId: string; deadline: DeadlineInput | null }) =>
      deadline
        ? api.put<Card>(`/v1/cards/${cardId}/deadline`, {
            date: deadline.date,
            time: deadline.time ?? null,
            start_date: deadline.start_date ?? null,
            start_time: deadline.start_time ?? null,
            ...(deadline.overdue_enabled !== undefined && {
              overdue_enabled: deadline.overdue_enabled,
            }),
          })
        : api.delete<Card>(`/v1/cards/${cardId}/deadline`),
    onSuccess: (card) => {
      client.setQueryData<CardDetail>(queryKeys.cards.detail(card.id), (current) =>
        current ? { ...current, ...card } : current,
      );
      syncCardInBoard(client, card);
    },
  });
}

export function useDeleteCard(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) => api.delete(`/v1/cards/${cardId}`),
    onSuccess: (_data, cardId) => {
      client.removeQueries({ queryKey: queryKeys.cards.detail(cardId) });
      updateDetail(client, boardId, (detail) => ({
        ...detail,
        cards: detail.cards.filter((c) => c.id !== cardId),
      }));
      void client.invalidateQueries({ queryKey: queryKeys.boards.trash(boardId) });
      void client.invalidateQueries({ queryKey: queryKeys.boards.archive(boardId) });
      // a deleted task (also one deleted from the archive) leaves every calendar
      void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

export function useSetCardPinned(boardId: string) {
  return useOptimisticBoardMutation(
    boardId,
    ({ cardId, pinned }: { cardId: string; pinned: boolean }) =>
      api.post<Card>(`/v1/cards/${cardId}/${pinned ? 'pin' : 'unpin'}`),
    (detail, { cardId, pinned }) => setPinnedLocally(detail, cardId, pinned),
  );
}

// --- labels ---------------------------------------------------------------

export function useCreateLabel(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; color: string }) =>
      api.post<Label>(`/v1/boards/${boardId}/labels`, data),
    onSuccess: (label) => {
      updateDetail(client, boardId, (detail) => ({ ...detail, labels: [...detail.labels, label] }));
    },
  });
}

export function useUpdateLabel(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ labelId, ...data }: { labelId: string; name?: string; color?: string }) =>
      api.patch<Label>(`/v1/labels/${labelId}`, data),
    onSuccess: (label) => {
      updateDetail(client, boardId, (detail) => ({
        ...detail,
        labels: detail.labels.map((l) => (l.id === label.id ? label : l)),
      }));
    },
  });
}

/** Board-level order (manager and filters); `order` is the resulting order, applied optimistically. */
export function useMoveBoardLabel(boardId: string) {
  return useOptimisticBoardMutation(
    boardId,
    ({
      labelId,
      anchor,
    }: {
      labelId: string;
      anchor: { after_label_id: string } | { before_label_id: string };
      order: string[];
    }) => api.post<Label[]>(`/v1/labels/${labelId}/move`, anchor),
    (detail, { order }) => ({
      ...detail,
      labels: order
        .map((id) => detail.labels.find((l) => l.id === id))
        .filter((l): l is Label => l !== undefined),
    }),
  );
}

export function useDeleteLabel(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (labelId: string) => api.delete(`/v1/labels/${labelId}`),
    onSuccess: (_data, labelId) => {
      updateDetail(client, boardId, (detail) => ({
        ...detail,
        labels: detail.labels.filter((l) => l.id !== labelId),
        cards: detail.cards.map((c) => ({
          ...c,
          label_ids: c.label_ids.filter((id) => id !== labelId),
        })),
      }));
      void client.invalidateQueries({ queryKey: queryKeys.cards.all });
    },
  });
}

/** Assign/unassign; optimistic on the board preview, server copy synced afterwards. */
export function useSetCardLabel(boardId: string) {
  const client = useQueryClient();
  return useOptimisticBoardMutation(
    boardId,
    async ({
      cardId,
      labelId,
      assigned,
    }: {
      cardId: string;
      labelId: string;
      assigned: boolean;
    }) => {
      const path = `/v1/cards/${cardId}/labels/${labelId}`;
      const card = await (assigned ? api.put<Card>(path) : api.delete<Card>(path));
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
      return card;
    },
    (detail, { cardId, labelId, assigned }) => ({
      ...detail,
      cards: detail.cards.map((c) => {
        if (c.id !== cardId) return c;
        const without = c.label_ids.filter((id) => id !== labelId);
        // Like the server: a new label is appended, the others keep their place.
        return { ...c, label_ids: assigned ? [...without, labelId] : without };
      }),
    }),
  );
}

/** Reorder one label inside this card (the order is per card, not per board). */
export function useMoveCardLabel(boardId: string) {
  const client = useQueryClient();
  return useOptimisticBoardMutation(
    boardId,
    async ({
      cardId,
      labelId,
      anchor,
    }: {
      cardId: string;
      labelId: string;
      anchor: { after_label_id: string } | { before_label_id: string };
      /** Resulting order of the card's labels, applied optimistically. */
      order: string[];
    }) => {
      const card = await api.post<Card>(`/v1/cards/${cardId}/labels/${labelId}/move`, anchor);
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
      return card;
    },
    (detail, { cardId, order }) => ({
      ...detail,
      cards: detail.cards.map((c) => (c.id === cardId ? { ...c, label_ids: order } : c)),
    }),
  );
}
